'use client';

import { signIn } from 'next-auth/react';
import { useRouter, useSearchParams } from 'next/navigation';
import { ROUTES } from '@/constants/routes';
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';

export function DevBypassLogin() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const devBypass = searchParams.get('dev_bypass') === 'true';
  const callbackUrl = searchParams.get('callbackUrl') || '/dashboard';

  useEffect(() => {
    if (!devBypass) return;

    const performDevLogin = async () => {
      setIsLoading(true);
      setError('');

      try {
        const result = await signIn('credentials', {
          email: 'test@example.com',
          password: 'password',
          redirect: false,
        });

        if (result?.error) {
          setError('Dev bypass login failed. Make sure you are in development mode.');
        } else if (result?.ok) {
          router.push(callbackUrl);
        }
      } catch {
        setError('An unexpected error occurred during dev bypass login.');
      } finally {
        setIsLoading(false);
      }
    };

    performDevLogin();
  }, [devBypass, callbackUrl, router]);

  if (!devBypass) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-card p-[var(--density-padding-lg)] rounded-lg shadow-lg max-w-sm w-full">
        <h2 className="text-[length:var(--density-font-lg)] font-semibold mb-[var(--density-spacing-sm)]">
          Development Mode Login
        </h2>

        {isLoading && (
          <div className="flex items-center justify-center py-[var(--density-padding-md)]">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            <span className="ml-2">Signing in with test credentials...</span>
          </div>
        )}

        {error && (
          <div className="p-3 rounded-md bg-red-50 border border-red-200 mb-[var(--density-spacing-sm)]">
            <p className="text-[length:var(--density-font-sm)] text-red-600">{error}</p>
          </div>
        )}

        <div className="text-[length:var(--density-font-sm)] text-muted-foreground">
          <p>Using test credentials:</p>
          <ul className="mt-2 space-y-1">
            <li>Email: test@example.com</li>
            <li>Password: password</li>
          </ul>
        </div>

        {!isLoading && error && (
          <button
            onClick={() => router.push(ROUTES.AUTH.SIGNIN)}
            className="mt-4 w-full px-[var(--density-padding-md)] py-2 bg-muted text-foreground rounded hover:bg-muted"
          >
            Go to regular login
          </button>
        )}
      </div>
    </div>
  );
}
